/**
 * When the preview moves from one playlist item to the next.
 *
 * Kept pure and free of the DOM so it can be tested directly, and so the rule
 * that decides how long something stays on screen lives in one readable place
 * rather than scattered through effects:
 *
 *   image  durationSec
 *   video  the asset's natural length x repeatCount
 *
 * The resolver re-runs every RESOLVE_INTERVAL_SEC. Most of those runs return the
 * same list of items, and restarting the playlist each time would be both wrong
 * and obvious to anyone watching, so `reconcileCursor` holds position whenever
 * the new list plays the same way as the old one.
 */

import type { ResolvedItem } from '@pbtv/shared';

export interface PlaybackCursor {
  /** Index into the resolved item list. */
  index: number;
  /** When the item at `index` started, on the same clock passed to `tickCursor`. */
  startedAtMs: number;
}

export function startCursor(nowMs: number): PlaybackCursor {
  return { index: 0, startedAtMs: nowMs };
}

/**
 * How long this item occupies the screen, in milliseconds, or null when that is
 * not knowable yet — an unmeasured video, where the player must wait for the
 * media element to report a duration or to fire `ended`.
 *
 * `measuredMs` is the duration the browser actually reported for the loaded
 * file. It wins over the manifest, which carries what the server probed at
 * upload time; when the two disagree the thing on screen is the truth.
 */
export function itemDurationMs(item: ResolvedItem, measuredMs: number | null = null): number | null {
  if (item.kind === 'image') {
    return Math.round(item.durationSec * 1000);
  }

  const natural = measuredMs ?? item.asset.durationMs;
  if (natural === null || !Number.isFinite(natural) || natural <= 0) return null;
  return Math.round(natural * item.repeatCount);
}

/** Milliseconds left on the current item, or null when the length is unknown. */
export function remainingMs(
  cursor: PlaybackCursor,
  items: readonly ResolvedItem[],
  nowMs: number,
  measuredMs: number | null = null,
): number | null {
  const current = items[cursor.index];
  if (current === undefined) return null;
  const duration = itemDurationMs(current, measuredMs);
  if (duration === null) return null;
  return Math.max(0, duration - (nowMs - cursor.startedAtMs));
}

/** Moves to the next item, wrapping at the end of the list. */
export function advanceCursor(
  cursor: PlaybackCursor,
  items: readonly ResolvedItem[],
  nowMs: number,
): PlaybackCursor {
  if (items.length === 0) return startCursor(nowMs);
  return { index: (cursor.index + 1) % items.length, startedAtMs: nowMs };
}

/**
 * One tick of the playback clock. Returns the cursor it was given, unchanged and
 * identical, when nothing is due — callers rely on that identity to avoid
 * re-rendering four times a second.
 */
export function tickCursor(
  cursor: PlaybackCursor,
  items: readonly ResolvedItem[],
  nowMs: number,
  measuredMs: number | null = null,
): PlaybackCursor {
  if (items.length === 0) return cursor;

  const current = items[cursor.index];
  if (current === undefined) return startCursor(nowMs);

  const duration = itemDurationMs(current, measuredMs);
  // Length not known yet: hold, and let the media element's `ended` event advance.
  if (duration === null) return cursor;

  if (nowMs - cursor.startedAtMs < duration) return cursor;
  return advanceCursor(cursor, items, nowMs);
}

/**
 * Identity of a list for the purpose of "is this still the same playback?".
 * Two lists match when they name the same assets in the same order with the same
 * timing — a re-resolve that produces an equivalent list must not restart it.
 */
export function playlistSignature(items: readonly ResolvedItem[]): string {
  return items
    .map((item) =>
      item.kind === 'image'
        ? `i:${item.assetId}:${item.durationSec}`
        : `v:${item.assetId}:${item.repeatCount}`,
    )
    .join('|');
}

/**
 * Carries the cursor across a re-resolve: kept when the list still plays the
 * same way, reset to the top when the content changed underneath it.
 */
export function reconcileCursor(
  cursor: PlaybackCursor,
  previous: readonly ResolvedItem[],
  next: readonly ResolvedItem[],
  nowMs: number,
): PlaybackCursor {
  if (next.length === 0) {
    return cursor.index === 0 ? cursor : startCursor(nowMs);
  }
  if (playlistSignature(previous) === playlistSignature(next) && cursor.index < next.length) {
    return cursor;
  }
  return startCursor(nowMs);
}
